const React = require('react');

// i18n
const translate = require('../../../../translation');

// Utils
const { sleep } = require('../../../../utils/sleep');
const { hasBankInterests } = require('../../../../utils/interests');
const { localeFromSiteId } = require('../../../../utils/locale');
const {
  createCardToken,
  mountSecureField,
  unmountSecureField,
  remountSecureField,
} = require('../utils/secure-fields');
const { logErrorFromClient } = require('../../../../utils/logTags');
const getBrowserInfo = require('../../../../utils/BrowserInfo');
const handleCardTokenization = require('../utils/card-tokenization');
const { sendMetricBasedOnTransactionType } = require('../utils/metricsHandler');

// Constants
const {
  APP: {
    SUMMARY_ITEMS_TYPES: { COUPON_DISCOUNT },
    SECURE_FIELDS: {
      ERROR_CAUSES: { INVALID_VALUE },
    },
    ERROR_SPA: { BROWSER_INFO_ERROR },
  },
  COMMONS: {
    PAYMENT_METHOD_ID: { CONSUMER_CREDITS, SPLIT_CARDS },
    PAYMENT_METHOD: { CREDIT_CARD, DEBIT_CARD, PREPAID_CARD },
  },
  METRICS: { ACTION_REVIEW_METRICS },
  SELECTORS: { ANDES_BUTTON_PAY_DESKTOP, ANDES_BUTTON_PAY_MOBILE },
} = require('../../../../../constants');

// Action Types
const {
  EXPRESS_RESET_SECURE_FIELDS,
  EXPRESS_SET_SECURE_FIELDS_ERROR,
  EXPRESS_SET_SECURE_FIELDS_FOCUS,
  EXPRESS_RESET_INSTALLMENTS,
  EXPRESS_SET_INSTALLMENTS_ERROR,
  EXPRESS_CARD_TOKEN,
  EXPRESS_RESET_CARD,
  LOADER_PAY_BUTTON,
  EXPRESS_SET_PAYMENT_CARD_ID,
} = require('../../../../spa/actions/types');
const { DEVICE_TYPE, ICONS_METHODS } = require('../../../../../constants/commons');
const { formatAmountWithInstallments } = require('../../../../utils/format');

const useExpress = ({
  originalProps,
  deviceType,
  translations,
  sdk,
  siteId,
  express,
  expressActions,
  loadingActions,
  internalDeviceId,
  paymentMethod,
  setPaymentMethod,
  paymentMethods,
  setPaymentMethods,
  cardToken,
  setCardToken,
  browserInfo,
  setBrowserInfo,
  setNextDueDate,
  showKycModal,
  setShowKycModal,
  setShowConsumerCreditsInfos,
  shouldRenderInstallments,
  shouldRenderSecurityCode,
  hasCCAPTokenizationFlagEnabled,
  smartTokenizationContext,
  transaction_type,
  payer,
  challengesInstructions,
  changePaymentMethod,
  reviewExpressPay,
  reviewExpressKyC,
  step_model,
}) => {
  const { locale, currency } = localeFromSiteId(siteId);

  const secureFieldActions = {
    resetSecureField: expressActions[EXPRESS_RESET_SECURE_FIELDS],
    setSecureFieldError: expressActions[EXPRESS_SET_SECURE_FIELDS_ERROR],
    setSecureFieldFocus: expressActions[EXPRESS_SET_SECURE_FIELDS_FOCUS],
  };

  const showBankInterests = () => hasBankInterests(siteId) && paymentMethod?.type === CREDIT_CARD;

  const isCombination = () => paymentMethod.id === SPLIT_CARDS;

  const getCustomPrice = () => {
    const installmentPrice = express?.installments?.[paymentMethod.option_id]?.price;
    const paymentMethodPrice = formatAmountWithInstallments(siteId, locale, currency, paymentMethod?.total_amount);
    return installmentPrice || paymentMethodPrice;
  };

  const getCustomSummaryItems = () => {
    const customSummaryItems = step_model.summary?.items?.filter((item) => item.type !== COUPON_DISCOUNT);

    const installmentIndex = express?.installments?.[paymentMethod.option_id]?.value;
    const isAccountMoneyBlack = paymentMethod?.id === ICONS_METHODS.ICON_ACCOUNT_MONEY_BLACK;
    // TODO DO NOT THIS, THIS SHOULD BE IMMUTABLE
    step_model.summary.total.detail = '';

    const isValidInstallment = installmentIndex && installmentIndex !== '' && !isAccountMoneyBlack;
    const installment = isValidInstallment ? paymentMethod.installments?.[installmentIndex - 1] : null;

    if (isValidInstallment && installment?.is_interest_free) {
      step_model.summary.total.detail = translations.NO_INTEREST();
    }

    return [...customSummaryItems, paymentMethod?.discount];
  };

  const handleConsumerCreditsChange = (paymentMethod) => {
    if (paymentMethod.id === CONSUMER_CREDITS) {
      setNextDueDate(paymentMethod.next_due_date);
      setShowConsumerCreditsInfos(true);
      return;
    }

    setNextDueDate(null);
    setShowConsumerCreditsInfos(false);
  };

  const handleSecureFieldChange = (paymentMethod) => {
    remountSecureField(paymentMethod, secureFieldActions);
  };

  const handleInstallmentsChange = () => {
    expressActions[EXPRESS_RESET_INSTALLMENTS](paymentMethod.option_id);
  };

  const handlePaymentMethodFields = (paymentMethod) => {
    handleInstallmentsChange();
    handleSecureFieldChange(paymentMethod);
    handleConsumerCreditsChange(paymentMethod);
    setPaymentMethod(paymentMethod);
    setShowKycModal(false);
  };

  const handlePaymentMethodChange = (paymentMethodId) => {
    const findPaymentMethod = paymentMethods?.find((paymentMethod) => paymentMethod.option_id === paymentMethodId);

    if (paymentMethodId === paymentMethod.option_id) {
      return;
    }

    if(paymentMethodId === SPLIT_CARDS) {
      setPaymentMethod(findPaymentMethod);
      return;
    }

    handlePaymentMethodFields(findPaymentMethod);
  };

  const handleFieldsValidation = async (securityCodeCardToken) => {
    let isSecurityCodeValid = true;
    let isInstallmentsValid = true;
    let err = null;

    const renderInstallments = shouldRenderInstallments(paymentMethod);
    const renderSecurityCode = shouldRenderSecurityCode(paymentMethod);

    if (!renderSecurityCode && !renderInstallments) {
      return true;
    }

    if (renderSecurityCode && !securityCodeCardToken) {
      if (hasCCAPTokenizationFlagEnabled) {
        const { error } = await smartTokenizationContext.handleTokenizationWithCCAP(paymentMethod);
        if (error) isSecurityCodeValid = false;
      } else {
        const { error } = await createCardToken();
        err = error;
      }

      if (err) {
        isSecurityCodeValid = false;
        secureFieldActions.setSecureFieldError(
          Array.isArray(err) && err.find((err) => err.cause === INVALID_VALUE)
            ? translations.CVV_IS_REQUIRED
            : translations.CVV_INCOMPLETE,
        );
      }
    }

    if (renderInstallments) {
      isInstallmentsValid = express?.installments?.[paymentMethod.option_id]?.value;
      expressActions[EXPRESS_SET_INSTALLMENTS_ERROR](paymentMethod.option_id, !isInstallmentsValid);
    }

    return isInstallmentsValid && isSecurityCodeValid;
  };

  const handleChangePaymentMethod = () => {
    sendMetricBasedOnTransactionType({transaction_type, device: deviceType, action: ACTION_REVIEW_METRICS.CHANGED});
    unmountSecureField();
    changePaymentMethod();
  };

  const handleSubmit = ({ action, cardTokenId, installments, tokenizationType }) => {
    const isWebview = originalProps.isWebview;
    const cardId = paymentMethod?.option_id;

    const payload = {
      card_token: cardTokenId,
      installments: String(installments),
      payer: {
        email: payer?.email?.value,
      },
      payment_method: {
        type: paymentMethod?.type,
        method: paymentMethod?.id,
        id: paymentMethod?.option_id,
      },
      token_generated_with_esc: tokenizationType
    };

    if (isWebview) {
      payload.browser_info = {
        browser_accept_header: browserInfo.accept_header,
        browser_color_depth: browserInfo.color_depth,
        browser_ip: browserInfo.ip,
        browser_java_enabled: browserInfo.java_enabled,
        browser_language: browserInfo.language,
        browser_screen_height: browserInfo.screen_height,
        browser_screen_width: browserInfo.screen_width,
        browser_timezone: browserInfo.timezone_offset,
        browser_user_agent: browserInfo.user_agent,
      };
    }
    expressActions[EXPRESS_SET_PAYMENT_CARD_ID](cardId);
    unmountSecureField();
    action(payload);
  };

  const handleKyc = () => {
    const cardId = paymentMethod?.option_id;
    const cardTokenId = expressActions[EXPRESS_CARD_TOKEN](cardId) || cardToken;
    const installments = express?.installments?.[cardId]?.value || 1;
    handleSubmit({ action: reviewExpressKyC, cardTokenId, installments });
  };

  const handlePayClick = async () => {
    const cardId = paymentMethod?.option_id;
    const installments = express?.installments?.[cardId]?.value || 1;
    let cardTokenId = expressActions[EXPRESS_CARD_TOKEN](cardId) || cardToken;
    let tokenization = null;

    if (!(await handleFieldsValidation(cardTokenId))) {
      setShowKycModal(false);
      return;
    }

    if (
      !cardTokenId &&
      (paymentMethod.type === CREDIT_CARD || paymentMethod.type === DEBIT_CARD || paymentMethod.type === PREPAID_CARD)
    ) {
      loadingActions[LOADER_PAY_BUTTON](true);

      const { cardTokenId: newCardTokenId, error, tokenizationType } = await handleCardTokenization({
        hasCCAPTokenizationFlagEnabled,
        handleTokenizationWithCCAP: smartTokenizationContext?.handleTokenizationWithCCAP,
        paymentMethod,
        cardId,
        internalDeviceId,
      });

      if (error) {
        loadingActions[LOADER_PAY_BUTTON](false);
        secureFieldActions.setSecureFieldError(translations.INVALID_CVV);
        return;
      }

      tokenization = tokenizationType;
      cardTokenId = newCardTokenId;
      setCardToken(cardTokenId);
      expressActions[EXPRESS_RESET_CARD](cardId);
    }

    setShowKycModal(true);

    if (challengesInstructions?.kyc) {
      if (!showKycModal) {
        sleep().then(() => {
          const buttonSelector = deviceType === DEVICE_TYPE.DESKTOP ? ANDES_BUTTON_PAY_DESKTOP : ANDES_BUTTON_PAY_MOBILE;
          document.querySelector(buttonSelector)?.click();
        });
      }

      loadingActions[LOADER_PAY_BUTTON](false);
      return;
    }

    sendMetricBasedOnTransactionType({transaction_type, device: deviceType, action: ACTION_REVIEW_METRICS.PAY});

    handleSubmit({ action: reviewExpressPay, cardTokenId, installments, tokenizationType: tokenization });
  };

  React.useEffect(() => {
    try {
      setBrowserInfo(getBrowserInfo(originalProps.initialStore));
    } catch (error) {
      logErrorFromClient(error, BROWSER_INFO_ERROR, '[express][3ds-browserinfo]');
    }
  }, []);

  React.useEffect(() => {
    sendMetricBasedOnTransactionType({transaction_type, device: deviceType, action: ACTION_REVIEW_METRICS.LOADED});

    if (window && window?.hj) {
      window.hj('event', 'review_express_redesigned');
    }
  }, []);

  React.useEffect(() => {
    const pms = originalProps.step_model?.combinations?.show_combination_row ? paymentMethods : originalProps.step_model.payment_methods;
    const pm = pms?.find((pm) => pm.option_id === paymentMethod.option_id);
    setPaymentMethods(pms);
    setPaymentMethod(pm);
  }, [originalProps.step_model.payment_methods]);

  React.useEffect(() => {
    if (sdk.loaded) {
      mountSecureField(paymentMethod, secureFieldActions);
    }
  }, [sdk.loaded]);

  return {
    secureFieldActions,
    showBankInterests,
    isCombination,
    getCustomPrice,
    getCustomSummaryItems,
    handleConsumerCreditsChange,
    handlePaymentMethodFields,
    handleSecureFieldChange,
    handleInstallmentsChange,
    handlePaymentMethodChange,
    handleFieldsValidation,
    handleChangePaymentMethod,
    handleSubmit,
    handleKyc,
    handlePayClick,
  };
};

module.exports = useExpress;